import { Component, ReactNode, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { trackEvent } from '@/shared/lib';
import { Button, Skeleton } from '@/shared/ui';

import { i18n } from '../config';
import { fetchKbPage, getKbLang, kbToc, type KbIrArticle } from '../lib';

import { KbIrRenderer } from './KbIrRenderer';
import { KbToc } from './KbToc';
import styles from './KnowledgeBase.module.scss';

type KbArticleProps = {
  // The market the article is fetched from — for links opened from the
  // article body this may differ from the panel tree's market.
  market: string;
  slug: string;
  locale?: string | null;
  onOpenArticle: (slug: string, market?: string) => void;
};

type PageStatus = 'idle' | 'loading' | 'ready' | 'error';

const ArticleError = ({ onRetry }: { onRetry: () => void }) => (
  <div className={styles.articlePlaceholder}>
    <p>
      <FormattedMessage {...i18n.articleError} />
    </p>
    <Button variant="secondary" onClick={onRetry}>
      <FormattedMessage {...i18n.retry} />
    </Button>
  </div>
);

// The hub validates nodes server-side, but if a contract-matching envelope
// ever carried a malformed node, the renderer throws during render — past
// KbArticle's fetch-only try/catch. This local boundary funnels such render
// crashes into the same article error UI. Keyed by attempt, so retry
// remounts it and clears the failure state.
class KbArticleBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

// Small article-shaped skeleton shown while the page loads: title line,
// excerpt line, a 16/10 media block and two body lines — mirrors the
// standalone article layout so the swap-in is calm.
export const ArticleSkeleton = () => (
  <div className={styles.articleSkeleton} aria-hidden="true">
    <Skeleton width="55%" height={30} borderRadius={8} />
    <Skeleton width="80%" height={16} borderRadius={6} />
    <Skeleton width="100%" height={220} borderRadius={8} />
    <Skeleton width="100%" height={14} borderRadius={6} />
    <Skeleton width="92%" height={14} borderRadius={6} />
  </div>
);

export const KbArticle = (props: KbArticleProps) => {
  const { market, slug, locale, onOpenArticle } = props;
  const [status, setStatus] = useState<PageStatus>('idle');
  const [page, setPage] = useState<KbIrArticle | null>(null);
  const [attempt, setAttempt] = useState(0);
  // The outline follows the page on screen. The memo keeps the array
  // identity stable: KbToc keys its scroll listener off it, and a fresh
  // array would re-register that listener on every render.
  const toc = useMemo(() => kbToc(page?.nodes ?? []), [page]);

  useEffect(() => {
    if (!slug) return;

    let active = true;
    setStatus('loading');

    fetchKbPage(market, slug, getKbLang(locale))
      .then((nextPage) => {
        if (!active) return;
        setPage(nextPage);
        setStatus('ready');
        trackEvent({
          eventName: 'KnowledgeBaseArticleOpened',
          properties: {
            kb_slug: nextPage.slug,
            kb_market: nextPage.market,
            kb_title: nextPage.title,
            language: nextPage.lang,
          },
        });
      })
      .catch(() => {
        if (!active) return;
        setStatus('error');
      });

    return () => {
      active = false;
    };
  }, [market, slug, locale, attempt]);

  if (!slug) {
    return (
      <div className={styles.articlePlaceholder}>
        <FormattedMessage {...i18n.chooseArticle} />
      </div>
    );
  }

  if (status === 'loading') {
    return <ArticleSkeleton />;
  }

  const retry = () => setAttempt((current) => current + 1);

  if (status === 'error') {
    return <ArticleError onRetry={retry} />;
  }

  // Hub article links are intercepted by the renderer and routed to
  // onOpenArticle; failed images collapse inside it too; malformed nodes
  // surface through the boundary as the article error UI. The title and
  // excerpt header mirrors the hub's standalone article page.
  return (
    <KbArticleBoundary
      key={attempt}
      fallback={<ArticleError onRetry={retry} />}
    >
      <div className={styles.articleLayout}>
        <div className={styles.articleBody}>
          {page && (
            <header className={styles.articleHeader}>
              <h1 className={styles.articleTitle}>{page.title}</h1>
              {page.excerpt && (
                <p className={styles.articleExcerpt}>{page.excerpt}</p>
              )}
            </header>
          )}
          <KbIrRenderer
            nodes={page?.nodes ?? []}
            onOpenArticle={onOpenArticle}
            article={{ market, slug }}
          />
        </div>
        <KbToc items={toc} />
      </div>
    </KbArticleBoundary>
  );
};
